import { useEffect, useState } from "react";
import type { Permission } from "@observe-tech/mfe-shell-api";
import { useShellStore } from "@observe-tech/mfe-shell-api/react";
import { useShellHost } from "@/shell-handle/ShellHostProvider";

export function TestMfePage() {
  const host = useShellHost();
  const { handle } = host;
  const sites = useShellStore(handle.sites.list);
  const lang = useShellStore(handle.i18n.language);
  const user = useShellStore(handle.auth.currentUser);

  const [permissions, setPermissions] = useState<Permission[]>(
    handle.permissions.raw(),
  );
  useEffect(() => {
    setPermissions(handle.permissions.raw());
    return handle.permissions.onChange(() => {
      setPermissions(handle.permissions.raw());
    });
  }, [handle]);

  const active = permissions.filter((p) => p.active);
  const inactive = permissions.filter((p) => !p.active);

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6 text-sm">
      <header>
        <h2 className="text-lg font-semibold">Test MFE mounted</h2>
        <p className="text-gray-600 mt-1">mode: {handle.mode}</p>
        <p className="text-gray-600">user: {user?.email ?? "(none)"}</p>
        <p className="text-gray-600">sites visible to user: {sites.length}</p>
        <p className="text-gray-600">current language: {lang}</p>
        <button
          type="button"
          className="mt-3 px-3 py-1 bg-blue-600 text-white rounded"
          onClick={() => handle.notify("Hello from test-mfe", "success")}
        >
          Fire toast
        </button>
      </header>

      <section>
        <h3 className="text-md font-semibold mb-2">
          Permissions ({permissions.length} total · {active.length} active ·{" "}
          {inactive.length} inactive)
        </h3>
        {permissions.length === 0 ? (
          <p className="text-gray-500">
            No permissions loaded. Make sure the user is logged in and the
            <code className="mx-1 px-1 bg-gray-100 rounded">
              getPermissions
            </code>
            query against nemo-auth-api succeeded.
          </p>
        ) : (
          <table className="w-full border border-gray-200 text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 border-b">Name</th>
                <th className="px-3 py-2 border-b">Active</th>
                <th className="px-3 py-2 border-b">Sites</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((p) => (
                <tr key={p.name} className={p.active ? "" : "text-gray-400"}>
                  <td className="px-3 py-1 border-b font-mono text-xs">
                    {p.name}
                  </td>
                  <td className="px-3 py-1 border-b">
                    {p.active ? "yes" : "no"}
                  </td>
                  <td className="px-3 py-1 border-b font-mono text-xs">
                    {p.sites.length === 0 ? "(none)" : p.sites.join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3 className="text-md font-semibold mb-2">Raw JSON</h3>
        <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-auto max-h-80">
          {JSON.stringify(permissions, null, 2)}
        </pre>
      </section>
    </div>
  );
}
